import { useContext, useState } from 'react'
import { Button } from '../Common/Button'
import { StatusChip } from '../Common'
import PersonAddRoundedIcon from '@mui/icons-material/PersonAddRounded'
import { useNls } from '../../nls/nlsProvider'
import { WorkspaceContext } from '../../context/workspaceContextCore'
import type { FieldPassSummaryCardMarker } from '../../types/chat'

export interface FieldPassSummaryCardProps {
  summary: FieldPassSummaryCardMarker
  canManage?: boolean
}

export const FieldPassSummaryCard = ({ summary, canManage: canManageProp }: FieldPassSummaryCardProps) => {
  const { t } = useNls()
  const workspaceContext = useContext(WorkspaceContext)
  const canManage = canManageProp ?? Boolean(
    workspaceContext?.canPerform?.('workspace.manage_invites') ||
    workspaceContext?.canPerform?.('workspace.manage_field_passes') ||
    workspaceContext?.activeRole === 'OWNER' ||
    workspaceContext?.activeRole === 'ADMIN',
  )

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [invited, setInvited] = useState(false)

  const handleMakeHelper = async () => {
    if (!summary.phone || !summary.workspaceId) return
    setBusy(true)
    setError(null)
    try {
      const { createHelperPhoneInvite } = await import('../../services/inviteService')
      await createHelperPhoneInvite({
        workspaceId: summary.workspaceId,
        phoneE164: summary.phone,
      })
      setInvited(true)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('NLSFieldPassHelperInviteFailed'))
    } finally {
      setBusy(false)
    }
  }

  const displayName = summary.displayName || t('NLSFieldPassFallbackName')

  return (
    <div
      className="mt-2 rounded-lg border border-[var(--border-subtle)] bg-[var(--surface-raised)] p-3 text-xs"
      data-testid="field-pass-summary-card"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold text-[var(--text-strong)]">
          {t('NLSFieldPassSummaryTitle', { name: displayName })}
        </span>
        <StatusChip tone="neutral" size="sm">
          {t('NLSFieldPassStatusClosedCleanedUp')}
        </StatusChip>
      </div>

      <div className="mt-2 grid gap-1 text-[var(--text-muted)]">
        {summary.phone ? (
          <div>
            <span className="font-medium text-[var(--text)]">{t('NLSFieldPassPhoneLabel')}{' '}</span>
            <span>{summary.phone}</span>
          </div>
        ) : null}
        <div>
          <span className="font-medium text-[var(--text)]">{t('NLSFieldPassTasksCompletedLabel')}{' '}</span>
          <span>{summary.doneCount}</span>
        </div>
        <div>
          <span className="font-medium text-[var(--text)]">{t('NLSFieldPassProofsSubmittedLabel')}{' '}</span>
          <span>{summary.proofCount}</span>
        </div>
        {summary.firstAt ? (
          <div className="text-[11px] text-[var(--muted)]">
            {t('NLSFieldPassActiveLabel', { date: new Date(summary.firstAt).toLocaleDateString() })}
          </div>
        ) : null}
      </div>

      {error ? (
        <p className="mt-2 text-xs text-[var(--danger)]" data-testid="make-helper-error">
          {error}
        </p>
      ) : null}

      {invited ? (
        <p className="mt-2 text-xs text-[var(--success)]" data-testid="make-helper-success">
          {t('NLSFieldPassHelperInviteSent', { phone: summary.phone ?? '' })}
        </p>
      ) : null}

      {canManage && summary.phone && !invited ? (
        <div className="mt-3 flex items-center justify-end">
          <Button
            variant="secondary"
            size="sm"
            icon={<PersonAddRoundedIcon fontSize="small" />}
            disabled={busy}
            loading={busy}
            data-testid="make-helper-btn"
            onClick={handleMakeHelper}
          >
            {t('NLSFieldPassMakeHelper')}
          </Button>
        </div>
      ) : null}
    </div>
  )
}
